import { memo, useState, useRef, useEffect } from "react";
import { Play, Pause } from "lucide-react";

interface VoiceMessagePlayerProps {
  audioUrl: string;
  duration?: number;
  isOwnMessage: boolean;
}

const VoiceMessagePlayer = memo(({ audioUrl, isOwnMessage }: VoiceMessagePlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.addEventListener("loadedmetadata", () => {
      setDuration(audio.duration);
    });

    audio.addEventListener("timeupdate", () => {
      setProgress((audio.currentTime / audio.duration) * 100);
    });

    audio.addEventListener("ended", () => {
      setIsPlaying(false);
      setProgress(0);
    });

    return () => {
      audio.pause();
      audio.src = "";
    };
  }, [audioUrl]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = clickPosition * audioRef.current.duration;
  };

  return (
    <div className="flex items-center gap-3 min-w-[180px]">
      <button
        onClick={togglePlay}
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          isOwnMessage
            ? "bg-white/20 hover:bg-white/30"
            : "bg-pop/20 hover:bg-pop/30"
        }`}
      >
        {isPlaying ? (
          <Pause className={`w-5 h-5 ${isOwnMessage ? "text-white" : "text-pop"}`} />
        ) : (
          <Play className={`w-5 h-5 ${isOwnMessage ? "text-white" : "text-pop"}`} />
        )}
      </button>
      
      <div className="flex-1 flex flex-col gap-1">
        <div 
          className="h-1.5 bg-white/20 rounded-full cursor-pointer overflow-hidden"
          onClick={handleProgressClick}
        >
          <div 
            className={`h-full rounded-full transition-all ${
              isOwnMessage ? "bg-white" : "bg-pop"
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className={`text-[10px] ${isOwnMessage ? "text-white/70" : "text-muted-foreground"}`}>
          {formatTime(audioRef.current?.currentTime || 0)} / {formatTime(duration)}
        </span>
      </div>
    </div>
  );
});

VoiceMessagePlayer.displayName = "VoiceMessagePlayer";

export default VoiceMessagePlayer;
